import React, { useState, useEffect, useRef } from 'react';
import { Search, CreditCard, Users, TrendingUp, BarChart3, PieChart, DollarSign, ChartPie, ArrowLeft, Download } from 'lucide-react';
import { analyticsService, type TableRow, type TableColumn, type TableConfig, CardConfig, MiniCardConfig, type ChartConfig, type ChartDataItem, type RowConfig } from '../../../services/analytics-service';
import { nudgesService, type CreateNudgeFormData } from '../../../services/nudges-service';
import type { NudgeFormErrors, NudgeItem } from '../../../types/nudges';
import { AnalyticsPortalTable } from './AnalyticsPortalTable';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { DrawerProvider, TabSwitcher, Button } from 'equal-ds-ui';
import { NudgeDrawer } from './NudgeDrawer';
import { NudgeEditDrawer } from './NudgeEditDrawer';
import { NudgesTable } from './NudgesTable';
import { AnalyticsCard } from './AnalyticsCard';
import { AnalyticsMiniCard } from './AnalyticsMiniCard';
import { AnalyticsPieChart } from './AnalyticsPieChart';
import { AnalyticsBarChart } from './AnalyticsBarChart';
import './analytics-portal.css';
import { ErrorBoundary } from '../../ErrorBoundary';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// Icon string mapping function
const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, any> = {
    CreditCard,
    Users,
    TrendingUp,
    BarChart3,
    PieChart,
    DollarSign,
    ChartPie
  };
  return iconMap[iconName] || Users;
};

const getLoggedInUserEmail = (): string => {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (encoded) {
      const userInfo = JSON.parse(atob(encoded));
      return userInfo.email || 'admin@company.com';
    }
  } catch (error) {
    console.error('Error getting user email:', error);
  }
  return 'admin@company.com';
};

/**
 * Simple cron expression validation
 * Validates basic 5-part cron format: minute hour day month weekday
 * Example: "0 9 * * MON" (every Monday at 9 AM)
 */
const validateCronExpression = (cronExpression: string): string | undefined => {
  if (!cronExpression || cronExpression.trim().length === 0) {
    return 'Cron expression is required';
  }

  const parts = cronExpression.trim().split(/\s+/);

  // Must have exactly 5 parts
  if (parts.length !== 5) {
    return 'Cron expression must have 5 parts (minute hour day month weekday)';
  }

  // Each part should be valid cron syntax (*, number, range, list, or step)
  const cronPartRegex = /^(\*|[0-9]|[0-9][0-9]|[0-9]-[0-9]|[0-9]-[0-9][0-9]|[0-9],[0-9]|\*\/[0-9]|[A-Z]{3}|[A-Z]{3}-[A-Z]{3})$/;

  const invalidParts = parts.filter(part => !cronPartRegex.test(part));
  if (invalidParts.length > 0) {
    return 'Invalid cron syntax. Use format: minute hour day month weekday (e.g., "0 9 * * MON")';
  }

  return undefined;
};

/**
 * Extract placeholders from message template
 * Example: "Hi {name}, your balance is {amount}" -> ['name', 'amount']
 */
const extractPlaceholders = (template: string): string[] => {
  const matches = template.match(/\{([^}]+)\}/g) || [];
  return matches.map(m => m.slice(1, -1));
};

/**
 * Validate that all placeholders in template are available in query columns
 * Only validates for QUERY_BASED targeting mode
 */
const validateTemplatePlaceholders = (
  template: string,
  targetingMode: string,
  availableColumns: string[]
): string | undefined => {
  // Only validate for query-based targeting
  if (targetingMode !== 'QUERY_BASED') {
    return undefined;
  }

  // Skip validation if no columns available yet (user hasn't run query)
  if (availableColumns.length === 0) {
    return undefined;
  }

  const usedPlaceholders = extractPlaceholders(template);
  const invalidPlaceholders = usedPlaceholders.filter(
    placeholder => !availableColumns.includes(placeholder)
  );

  if (invalidPlaceholders.length > 0) {
    return `Invalid placeholders: {${invalidPlaceholders.join('}, {')}}. Available columns: ${availableColumns.join(', ')}`;
  }

  return undefined;
};

// Number of rows in backend layout — kept in sync with analytics-dashboard-config.ts
const defaultLayout_rowCount = 3;

export const AnalyticsPortalPage: React.FC = () => {
  // Mounted ref — prevents setState on unmounted component
  const mountedRef = useRef(true);

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'nudges'>('overview');

  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rowLoading, setRowLoading] = useState<Record<number, boolean>>({ 0: true, 1: true, 2: true });

  const [searchTerm, _setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Search functionality states
  const [searchMode, setSearchMode] = useState<'NLP' | 'Query'>('NLP');
  const [searchInput, setSearchInput] = useState<string>('');
  const [lastExecutedQuery, setLastExecutedQuery] = useState<string>('');
  const [searchError, setSearchError] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [chatId] = useState<string>(`chat-${Date.now()}`);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [tableId, setTableId] = useState<string>('');

  const [allItems, setAllItems] = useState<Array<{ type: 'table' | 'card_row' | 'mini_card_row' | 'chart_row' | 'mixed_row'; data: any }>>([]);

  // Charts state management
  const [chartDataMap, setChartDataMap] = useState<Map<string, ChartConfig>>(new Map());
  const [activeButtonMap, setActiveButtonMap] = useState<Map<string, string>>(new Map());
  const [loadingCharts, setLoadingCharts] = useState<Set<string>>(new Set());

  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  // Nudges state management
  const [nudges, setNudges] = useState<NudgeItem[]>([]);
  const [isLoadingNudges, setIsLoadingNudges] = useState<boolean>(false);
  const [_nudgesError, setNudgesError] = useState<string | null>(null);
  const [_showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [nudgeDrawerOpen, setNudgeDrawerOpen] = useState<boolean>(false);
  const [nudgeEditDrawerOpen, setNudgeEditDrawerOpen] = useState<boolean>(false);
  const [isCreatingNudge, setIsCreatingNudge] = useState<boolean>(false);
  const [isExecutingNudge, setIsExecutingNudge] = useState<string | null>(null);
  const [isDeletingNudge, setIsDeletingNudge] = useState<string | null>(null);
  const [editingNudge, setEditingNudge] = useState<NudgeItem | null>(null);
  const [isUpdatingNudge, setIsUpdatingNudge] = useState<boolean>(false);

  // Nudge form state
  const [nudgeForm, setNudgeForm] = useState<CreateNudgeFormData>({
    nudge_name: '',
    message_template: '',
    targeting_mode: 'QUERY_BASED',
    user_targets: [{ user_id: '', user_placeholders: {} }],
    sql_query: '',
    notification_type: 'ON_DEMAND',
    scheduled_date_time: '',
    cron_expression: '',
    created_by: getLoggedInUserEmail()
  });
  const [formErrors, setFormErrors] = useState<NudgeFormErrors>({});

  // JSON editing state for user targets
  const [jsonTextState, setJsonTextState] = useState<Record<number, string>>({});
  const [jsonValidationErrors, setJsonValidationErrors] = useState<Record<number, string>>({});

  // Query search state for NudgeDrawer
  const [queryResults, setQueryResults] = useState<TableRow[]>([]);
  const [queryColumns, setQueryColumns] = useState<TableColumn[]>([]);
  const [isSearchingQuery, setIsSearchingQuery] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<string>('');

  // Drill-down state for clickable cards
  const [drillDownCardId, setDrillDownCardId] = useState<string | null>(null);
  const [drillDownTitle, setDrillDownTitle] = useState<string>('');
  const [drillDownColumns, setDrillDownColumns] = useState<TableColumn[]>([]);
  const [drillDownRows, setDrillDownRows] = useState<TableRow[]>([]);
  const [drillDownTotalRows, setDrillDownTotalRows] = useState<number>(0);
  const [drillDownPage, setDrillDownPage] = useState<number>(1);
  const [drillDownPageSize, setDrillDownPageSize] = useState<number>(10);
  const [isDrillDownLoading, setIsDrillDownLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Button click handler for V2 charts
  const handleChartButtonClick = async (chartId: string, buttonId: string) => {
    // Prevent concurrent requests for the same chart
    if (loadingCharts.has(chartId)) {
      return;
    }

    // Don't reload if already showing this data
    if (activeButtonMap.get(chartId) === buttonId) {
      return;
    }

    // Mark chart as loading
    setLoadingCharts(prev => new Set(prev).add(chartId));

    try {
      const response = await analyticsService.fetchChartData(buttonId);

      // Backend returns nested data structure: { data: ChartDataItem[] }
      const responseData = response.data as unknown as { data: ChartDataItem[] };
      const chartData = responseData.data;

      if (!chartData || !Array.isArray(chartData)) {
        console.error('Invalid chart data format:', response.data);
        return;
      }

      // Get existing chart from chartDataMap (should always exist after initialization)
      const existingChart = chartDataMap.get(chartId);

      if (!existingChart) {
        console.error('Chart not found in chartDataMap:', chartId);
        return;
      }

      // Create updated chart with new data
      const updatedChart: ChartConfig = {
        ...existingChart,
        data: chartData
      };

      // Update both maps atomically
      setChartDataMap(prev => {
        const newMap = new Map(prev);
        newMap.set(chartId, updatedChart);
        return newMap;
      });

      setActiveButtonMap(prev => {
        const newMap = new Map(prev);
        newMap.set(chartId, buttonId);
        return newMap;
      });
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      // Remove chart from loading set
      setLoadingCharts(prev => {
        const newSet = new Set(prev);
        newSet.delete(chartId);
        return newSet;
      });
    }
  };

  // Drill-down handlers — state changes trigger the useEffect below
  const handleCardClick = (cardId: string, title: string) => {
    setDrillDownCardId(cardId);
    setDrillDownTitle(title);
    setDrillDownPage(1);
    setDrillDownPageSize(10);
  };

  const handleDrillDownBack = () => {
    setDrillDownCardId(null);
    setDrillDownTitle('');
    setDrillDownColumns([]);
    setDrillDownRows([]);
    setDrillDownTotalRows(0);
    setDrillDownPage(1);
    setDrillDownPageSize(10);
  };

  // Drill-down error state
  const [drillDownError, setDrillDownError] = useState<string | null>(null);
  // Download error state
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadCSV = async () => {
    if (!drillDownCardId) return;
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const result = await analyticsService.downloadCardReport(drillDownCardId);
      if (!mountedRef.current) return;
      if (!result.csv) {
        setDownloadError('No data available to download');
        return;
      }

      // Sanitize filename — strip unsafe chars, control chars, enforce .csv extension
      const rawName = result.filename || `${drillDownCardId}_report.csv`;
      const stripped = rawName.replace(/[\/\\:*?"<>|\x00-\x1f\x7f]/g, '_').slice(0, 200);
      const safeName = stripped.endsWith('.csv') ? stripped : stripped.replace(/\.[^.]+$/, '') + '.csv';

      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Error downloading CSV:', error);
      setDownloadError('Failed to download CSV. Please try again.');
    } finally {
      if (mountedRef.current) setIsDownloading(false);
    }
  };

  // Retry drill-down fetch — called directly from the Retry button
  const retryDrillDown = async () => {
    if (!drillDownCardId) return;
    setIsDrillDownLoading(true);
    setDrillDownError(null);
    try {
      const result = await analyticsService.fetchCardDetail(drillDownCardId, drillDownPage, drillDownPageSize);
      if (!mountedRef.current) return;
      setDrillDownColumns(result.columns);
      setDrillDownRows(result.rows);
      setDrillDownTotalRows(result.totalRows);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Retry error:', err);
      setDrillDownError('Failed to load data. Please try again.');
    } finally {
      if (mountedRef.current) setIsDrillDownLoading(false);
    }
  };

  // Drill-down fetch effect — handles both initial click and pagination changes
  useEffect(() => {
    if (!drillDownCardId) return;

    let cancelled = false;

    const fetchDrillDownPage = async () => {
      setIsDrillDownLoading(true);
      setDrillDownError(null);
      try {
        const result = await analyticsService.fetchCardDetail(drillDownCardId, drillDownPage, drillDownPageSize);
        if (cancelled) return;
        setDrillDownColumns(result.columns);
        setDrillDownRows(result.rows);
        setDrillDownTotalRows(result.totalRows);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching drill-down page:', error);
        setDrillDownError('Failed to load data. Please try again.');
        setDrillDownColumns([]);
        setDrillDownRows([]);
        setDrillDownTotalRows(0);
      } finally {
        if (!cancelled) setIsDrillDownLoading(false);
      }
    };

    fetchDrillDownPage();
    return () => { cancelled = true; };
  }, [drillDownCardId, drillDownPage, drillDownPageSize]);

  const renderTableRow = (item: TableConfig, rowIndex: number) => {
    const tableRows = item.rows || [];
    // Use tableColumns state if available (from search), otherwise use item.columns (from initial load)
    const columns = tableColumns.length > 0 ? tableColumns : (item.columns || []);

    if (tableRows.length === 0) {
      return null;
    }

    return (
      <div key={`table-row-${rowIndex}`} className="bg-white rounded-lg border shadow-sm">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-semibold leading-none tracking-tight mb-1">{item.title}</h3>
              <p className="text-sm text-gray-600 text-left">
                {`Showing ${totalRows || item.totalRows || tableData.length} users`}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <div className="flex gap-2">

              <select
                value={searchMode}
                onChange={(e) => {
                  setSearchMode(e.target.value as 'NLP' | 'Query');
                  setSearchError('');
                  setSearchInput('');
                  setLastExecutedQuery('');
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#21C45D] min-w-[100px]"
              >
                <option value="NLP">Text Search</option>
                <option value="Query">Search</option>
              </select>

              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={
                    searchMode === 'NLP'
                      ? 'Ask a question (e.g., "Show me users with salary above 50000")'
                      : 'Enter SQL query (e.g., "SELECT user_id, name, email FROM users WHERE active = true LIMIT 10")'
                  }
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setSearchError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isSearching) {
                      handleSearch();
                    }
                  }}
                  className="w-full pl-14 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#21C45D]"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className={`px-8 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap text-base ${!isSearching
                  ? 'bg-[#21C45D] text-white hover:bg-[#1DB056] cursor-pointer shadow-sm'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Error Message */}
            {searchError && (
              <p className="text-red-600 text-sm">{searchError}</p>
            )}
          </div>

          {/* Table with AnalyticsPortalTable Component */}
          <AnalyticsPortalTable
            title={item.title}
            columns={columns as TableColumn[]}
            data={tableData as TableRow[]}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalRows || tableData.length}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            isLoading={isLoading}
          />

        </div>
      </div>
    );
  };



  const ChartWrapper: React.FC<{
    chartConfig: ChartConfig;
    chartContent: React.ReactNode;
    onButtonClick?: (buttonId: string) => Promise<void>;
    activeButtonId: string;
    isLoading?: boolean;
  }> = ({ chartConfig, chartContent, onButtonClick, activeButtonId, isLoading = false }) => {
    const IconComponent = getIconComponent(chartConfig.icon);

    const handleButtonClick = async (buttonId: string) => {
      if (isLoading || buttonId === activeButtonId) return;

      if (onButtonClick) {
        try {
          await onButtonClick(buttonId);
        } catch (error) {
          console.error('Error handling button click:', error);
        }
      }
    };

    const overallColor = chartConfig.overall_color;

    return (
      <div className="bg-white rounded border shadow-sm p-3 text-left w-full">
        {/* Header with Icon and Title */}
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 rounded-full bg-white flex items-center justify-center border-2"
            style={{ borderColor: overallColor }}
          >
            <IconComponent className="w-4 h-4 text-black" />
          </div>
          <h3 className="text-lg font-bold leading-snug text-black tracking-normal">
            {chartConfig.overall_title}
          </h3>
        </div>

        {/* Optional Buttons - Always reserve space even if no buttons */}
        <div className="flex gap-2 mb-3 min-h-[28px] overflow-x-auto">
          {chartConfig.hasButtons && chartConfig.buttons.length > 0 && (
            <>
              {chartConfig.buttons.map((button) => {
                const isSelected = activeButtonId === button.id;

                return (
                  <Button
                    key={button.id}
                    variant={isSelected ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => handleButtonClick(button.id)}
                    disabled={isLoading || isSelected}
                    style={isSelected ? { backgroundColor: '#000', borderColor: '#000' } : undefined}
                  >
                    {button.title}
                  </Button>
                );
              })}
            </>
          )}

        </div>

        {/* Chart Content */}
        {isLoading ? (
          // Skeleton loader while fetching new chart data
          <div className="animate-pulse space-y-4">
            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
            <div className="h-5 bg-gray-200 rounded w-1/2"></div>
            <div className="h-64 bg-gray-100 rounded mt-6"></div>
          </div>
        ) : (
          chartContent
        )}
      </div>
    );
  };


  const classifyRow = (row: RowConfig): { type: 'table' | 'card_row' | 'mini_card_row' | 'chart_row' | 'mixed_row'; data: any } | null => {
    const allCharts = row.items.every((item: any) =>
      item.type === 'pie_chart' || item.type === 'bar_chart'
    );
    const itemTypes = new Set(row.items.map((item: any) => item.type));

    if (allCharts) {
      return { type: 'chart_row', data: row.items };
    } else if (itemTypes.size === 1) {
      const singleType = row.items[0].type;
      if (singleType === 'card') return { type: 'card_row', data: row.items };
      if (singleType === 'mini_card') return { type: 'mini_card_row', data: row.items };
      if (singleType === 'table') return { type: 'table', data: row.items[0] as TableConfig };
      return { type: 'mixed_row', data: row.items }; // Fallback for unknown single-type rows
    } else {
      return { type: 'mixed_row', data: row.items };
    }
  };

  // Generation counter for loadAnalytics — prevents stale callbacks from re-invocations
  const loadGenerationRef = useRef(0);
  // Track whether initial load just completed — skip the first trigger to avoid duplicate fetch
  const initialLoadJustCompleted = useRef(false);

  const loadAnalytics = async () => {
    // Increment generation to invalidate any previous in-flight callbacks
    const currentGeneration = ++loadGenerationRef.current;
    // Reset so the skip-first-trigger logic works on subsequent reloads
    initialLoadJustCompleted.current = false;

    const rowCount = defaultLayout_rowCount;
    const initialLoading: Record<number, boolean> = {};
    const initialSlots: Array<any> = [];
    for (let i = 0; i < rowCount; i++) {
      initialLoading[i] = true;
      initialSlots.push(null);
    }
    setRowLoading(initialLoading);
    setIsLoading(true);
    setAllItems(initialSlots);

    let completedCount = 0;

    // Fire all row requests in parallel
    for (let i = 0; i < rowCount; i++) {
      analyticsService.fetchAnalyticsRow(i)
        .then(row => {
          if (!mountedRef.current || loadGenerationRef.current !== currentGeneration) return;
          if (row) {
            const processed = classifyRow(row);
            if (processed) {
              // Initialize chart maps for chart rows
              if (processed.type === 'chart_row') {
                const charts = processed.data as ChartConfig[];
                setChartDataMap(prev => {
                  const newMap = new Map(prev);
                  charts.forEach(chart => newMap.set(chart.id, chart));
                  return newMap;
                });
                setActiveButtonMap(prev => {
                  const newMap = new Map(prev);
                  charts.forEach(chart => newMap.set(chart.id, chart.id));
                  return newMap;
                });
              }

              // Initialize table state for table rows
              if (processed.type === 'table') {
                const tableConfig = processed.data as TableConfig;
                if (tableConfig.rows && tableConfig.columns) {
                  setTableData(tableConfig.rows);
                  setTableColumns(tableConfig.columns);
                  setTotalRows(tableConfig.totalRows || tableConfig.rows.length);
                  setTableId(tableConfig.id);
                }
              }

              // Update items AND loading in the same microtask so React batches them
              setAllItems(prev => {
                const updated = [...prev];
                updated[i] = processed;
                return updated;
              });
            }
          }
          // Mark row as loaded in same .then() — React 18 batches with setAllItems above
          setRowLoading(prev => ({ ...prev, [i]: false }));
        })
        .catch(err => {
          if (!mountedRef.current || loadGenerationRef.current !== currentGeneration) return;
          console.error(`Error loading row ${i}:`, err);
          setRowLoading(prev => ({ ...prev, [i]: false }));
        })
        .finally(() => {
          if (!mountedRef.current || loadGenerationRef.current !== currentGeneration) return;
          completedCount++;
          if (completedCount === rowCount) {
            setIsLoading(false);
            setInitialLoadComplete(true);
          }
        });
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const loadNudges = async () => {
      try {
        setIsLoadingNudges(true);
        setNudgesError(null);

        const response = await nudgesService.getNudges();
        if (response.status?.toLowerCase() === 'success') {
          setNudges(response.data || []);
        } else {
          setNudgesError(response.message);
          setNudges([]);
        }
      } catch (error) {
        console.error('Error loading nudges:', error);
        setNudgesError(`Failed to load nudges: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setNudges([]);
      } finally {
        setIsLoadingNudges(false);
      }
    };

    loadAnalytics();
    loadNudges();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!initialLoadComplete) {
      return;
    }

    // Skip the very first trigger when initialLoadComplete flips to true
    // (loadAnalytics already fetched this data)
    if (!initialLoadJustCompleted.current) {
      initialLoadJustCompleted.current = true;
      return;
    }

    if (lastExecutedQuery) {
      return;
    }

    if (!tableId) {
      return;
    }

    let cancelled = false;

    const fetchPage = async () => {
      try {
        setIsLoading(true);
        const response = await analyticsService.fetchTablePage(tableId, currentPage, pageSize);

        if (cancelled) return;
        const rows = response.rows || [];
        const columns = response.columns || [];
        const total = response?.totalRows ?? rows.length;

        setTableData(rows);
        setTableColumns(columns);
        setTotalRows(total);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching table page:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchPage();
    return () => { cancelled = true; };
  }, [currentPage, pageSize, tableId, lastExecutedQuery, initialLoadComplete]);

  const handleSearch = async () => {
    setSearchError('');
    setIsSearching(true);

    // Define max safe result size
    const MAX_SAFE_RESULTS = 10000;

    try {
      if (searchMode === 'NLP') {
        const response = await analyticsService.querySearch(searchInput, chatId);

        const sqlQuery = response.data.query;
        const searchRows = response.data.rows;
        const searchColumns = response.data.columns;
        const totalRows = response.data.totalRows || searchRows.length;

        // Warn if result set is very large
        if (totalRows > MAX_SAFE_RESULTS) {
          setSearchError(`Warning: Query returned ${totalRows.toLocaleString()} rows. This may affect performance. Consider refining your query.`);
        }

        setTableData(searchRows);
        setTableColumns(searchColumns);
        setTotalRows(totalRows);

        setLastExecutedQuery(sqlQuery);

        setCurrentPage(1);
      } else {
        const response = await analyticsService.directSearch(searchInput);

        if (!response.data || response.data.length === 0) {
          throw new Error('No data in response');
        }

        const firstRow = response.data[0];
        if (!firstRow.items || firstRow.items.length === 0) {
          throw new Error('No items in first row');
        }

        const tableItem = firstRow.items.find(item => item.type === 'table');
        if (!tableItem || tableItem.type !== 'table') {
          throw new Error('No table data in response');
        }

        const totalRows = tableItem.totalRows || tableItem.rows.length;

        // Warn if result set is very large
        if (totalRows > MAX_SAFE_RESULTS) {
          setSearchError(`Warning: Query returned ${totalRows.toLocaleString()} rows. This may affect performance. Consider refining your query.`);
        }

        setTableData(tableItem.rows);
        setTableColumns(tableItem.columns);
        setTotalRows(totalRows);

        setLastExecutedQuery(searchInput);

        setCurrentPage(1);
      }

    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const validateNudgeForm = (): boolean => {
    const errors: NudgeFormErrors = {};

    if (!nudgeForm.nudge_name.trim()) {
      errors.nudge_name = 'Nudge name is required';
    }

    if (!nudgeForm.message_template.trim()) {
      errors.message_template = 'Message template is required';
    } else {
      // Validate placeholders in message template for query-based targeting
      const availableColumnNames = queryColumns.map(col => col.key);
      const placeholderError = validateTemplatePlaceholders(
        nudgeForm.message_template,
        nudgeForm.targeting_mode,
        availableColumnNames
      );
      if (placeholderError) {
        errors.message_template = placeholderError;
      }
    }

    if (nudgeForm.targeting_mode === 'DIRECT_USER_IDS') {
      if (!nudgeForm.user_targets || nudgeForm.user_targets.length === 0) {
        errors.user_targets = 'At least one user target is required';
      } else {
        const hasValidTarget = nudgeForm.user_targets.some(target => target.user_id.trim());
        if (!hasValidTarget) {
          errors.user_targets = 'At least one valid user ID is required';
        }
      }
    } else if (nudgeForm.targeting_mode === 'QUERY_BASED') {
      if (!nudgeForm.sql_query?.trim()) {
        errors.sql_query = 'SQL query is required for query-based targeting';
      }
    }

    if (nudgeForm.notification_type === 'SCHEDULED') {
      if (!nudgeForm.scheduled_date_time) {
        errors.scheduled_date_time = 'Scheduled date and time is required';
      } else {
        // Validate that scheduled time is in the future
        const scheduledDate = new Date(nudgeForm.scheduled_date_time);
        const now = new Date();
        if (scheduledDate <= now) {
          errors.scheduled_date_time = 'Scheduled time must be in the future';
        }
      }
    } else if (nudgeForm.notification_type === 'RECURRING') {
      if (!nudgeForm.cron_expression?.trim()) {
        errors.cron_expression = 'Cron expression is required for recurring nudges';
      } else {
        const cronError = validateCronExpression(nudgeForm.cron_expression);
        if (cronError) {
          errors.cron_expression = cronError;
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatDateTimeForApi = (dateTime: string | undefined): string | undefined => {
    if (!dateTime) return dateTime;
    if (dateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      return `${dateTime}:00`;
    }
    return dateTime;
  };

  const handleCreateNudge = async () => {
    if (!validateNudgeForm()) return;

    try {
      setIsCreatingNudge(true);
      const formDataForApi = {
        ...nudgeForm,
        scheduled_date_time: formatDateTimeForApi(nudgeForm.scheduled_date_time),
        ...(nudgeForm.targeting_mode === 'QUERY_BASED' ? { user_targets: undefined } : {})
      };
      const response = await nudgesService.createNudge(formDataForApi);

      if (response.status?.toLowerCase() === 'success') {
        setNudgeForm({
          nudge_name: '',
          message_template: '',
          targeting_mode: 'DIRECT_USER_IDS',
          user_targets: [{ user_id: '', user_placeholders: {} }],
          sql_query: '',
          notification_type: 'ON_DEMAND',
          scheduled_date_time: '',
          cron_expression: '',
          created_by: getLoggedInUserEmail()
        });
        setFormErrors({});
        setShowCreateForm(false);
        setNudgeDrawerOpen(false); 

        const nudgesResponse = await nudgesService.getNudges();
        if (nudgesResponse.status?.toLowerCase() === 'success') {
          setNudges(nudgesResponse.data || []);
        }
      }
    } catch (error) {
      console.error('Error creating nudge:', error);
      setFormErrors({ message_template: 'Failed to create nudge. Please try again.' });
    } finally {
      setIsCreatingNudge(false);
    }
  };

  const handleExecuteNudge = async (nudgeId: string) => {
    try {
      setIsExecutingNudge(nudgeId);
      await nudgesService.executeNudge(nudgeId);

      const nudgesResponse = await nudgesService.getNudges();
      if (nudgesResponse.status?.toLowerCase() === 'success') {
        setNudges(nudgesResponse.data || []);
      }
    } catch (error) {
      console.error('Error executing nudge:', error);
    } finally {
      setIsExecutingNudge(null);
    }
  };

  const handleCloseEditDrawer = () => {
    setNudgeEditDrawerOpen(false);
    setEditingNudge(null);
    setFormErrors({});

    // Clear form data
    setNudgeForm({
      nudge_name: '',
      message_template: '',
      targeting_mode: 'QUERY_BASED',
      user_targets: [{ user_id: '', user_placeholders: {} }],
      sql_query: '',
      notification_type: 'ON_DEMAND',
      scheduled_date_time: '',
      cron_expression: '',
      created_by: getLoggedInUserEmail()
    });

    // Clear JSON state
    setJsonTextState({});
    setJsonValidationErrors({});
  };

  const handleEditNudge = (nudge: NudgeItem) => {
    if (nudgeEditDrawerOpen && editingNudge?.nudge_id === nudge.nudge_id) {
      handleCloseEditDrawer();
      return;
    }

    setEditingNudge(nudge);

    const targets = nudge.user_targets || [{ user_id: '', user_placeholders: {} }];

    setNudgeForm({
      nudge_name: nudge.nudge_name,
      message_template: nudge.message_template,
      targeting_mode: nudge.targeting_mode,
      user_targets: targets,
      sql_query: nudge.sql_query || '',
      notification_type: nudge.notification_type,
      scheduled_date_time: nudge.scheduled_date_time || '',
      cron_expression: nudge.cron_expression || '',
      created_by: nudge.created_by
    });

    initializeJsonText(targets);

    setNudgeEditDrawerOpen(true);
    setFormErrors({});
  };

  const handleUpdateNudge = async () => {
    if (!editingNudge || !validateNudgeForm()) return;

    try {
      setIsUpdatingNudge(true);
      const formDataForApi = {
        ...nudgeForm,
        scheduled_date_time: formatDateTimeForApi(nudgeForm.scheduled_date_time),
        ...(nudgeForm.targeting_mode === 'QUERY_BASED' ? { user_targets: undefined } : {}),
        status: editingNudge.status ?? 'ACTIVE'
      };
      await nudgesService.updateNudge(editingNudge.nudge_id, formDataForApi);

      // Close drawer and clear state
      handleCloseEditDrawer();

      const nudgesResponse = await nudgesService.getNudges();
      if (nudgesResponse.status?.toLowerCase() === 'success') {
        setNudges(nudgesResponse.data || []);
      }
    } catch (error) {
      console.error('Error updating nudge:', error);
      setFormErrors({ message_template: 'Failed to update nudge. Please try again.' });
    } finally {
      setIsUpdatingNudge(false);
    }
  };

  const handleDeleteNudge = async (nudgeId: string) => {
    setIsDeletingNudge(nudgeId);

    try {
      await nudgesService.deleteNudge(nudgeId);

      // Refresh nudges list after deletion
      const nudgesResponse = await nudgesService.getNudges();
      if (nudgesResponse.status?.toLowerCase() === 'success') {
        setNudges(nudgesResponse.data || []);
      }
    } catch (error) {
      console.error('Failed to delete nudge:', error);
    } finally {
      setIsDeletingNudge(null);
    }
  };


  const addUserTarget = () => {
    const newTargets = [...(nudgeForm.user_targets || []), { user_id: '', user_placeholders: {} }];
    setNudgeForm({
      ...nudgeForm,
      user_targets: newTargets
    });

    const newIndex = newTargets.length - 1;
    setJsonTextState(prev => ({
      ...prev,
      [newIndex]: '{}'
    }));
  };

  const updateUserTarget = (index: number, field: 'user_id' | 'user_placeholders', value: string | Record<string, string>) => {
    const updatedTargets = [...(nudgeForm.user_targets || [])];
    if (field === 'user_id') {
      updatedTargets[index].user_id = value as string;
    } else {
      updatedTargets[index].user_placeholders = value as Record<string, string>;
    }
    setNudgeForm({ ...nudgeForm, user_targets: updatedTargets });
  };

  const removeUserTarget = (index: number) => {
    const updatedTargets = (nudgeForm.user_targets || []).filter((_, i) => i !== index);
    setNudgeForm({ ...nudgeForm, user_targets: updatedTargets });

    const newJsonTextState = { ...jsonTextState };
    const newJsonValidationErrors = { ...jsonValidationErrors };
    delete newJsonTextState[index];
    delete newJsonValidationErrors[index];
    setJsonTextState(newJsonTextState);
    setJsonValidationErrors(newJsonValidationErrors);
  };

  const getJsonText = (index: number): string => {
    if (jsonTextState[index] !== undefined) {
      return jsonTextState[index];
    }
    const target = nudgeForm.user_targets?.[index];
    if (target?.user_placeholders) {
      return JSON.stringify(target.user_placeholders, null, 2);
    }
    return '{}';
  };

  const validateAndUpdateJson = (index: number, text: string) => {
    setJsonTextState(prev => ({ ...prev, [index]: text }));

    try {
      const parsed = JSON.parse(text);

      // Validate that it's an object (not array or primitive)
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        setJsonValidationErrors(prev => ({
          ...prev,
          [index]: 'Must be a valid JSON object'
        }));
        return;
      }

      // Validate that all values are strings
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value !== 'string') {
          setJsonValidationErrors(prev => ({
            ...prev,
            [index]: `Value for '${key}' must be a string`
          }));
          return;
        }
      }

      // All validations passed - update the form
      updateUserTarget(index, 'user_placeholders', parsed);
      setJsonValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } catch (error) {
      setJsonValidationErrors(prev => ({
        ...prev,
        [index]: 'Invalid JSON format'
      }));
    }
  };

  const initializeJsonText = (targets: any[]) => {
    const newJsonTextState: Record<number, string> = {};
    targets.forEach((target, index) => {
      newJsonTextState[index] = JSON.stringify(target.user_placeholders || {}, null, 2);
    });
    setJsonTextState(newJsonTextState);
    setJsonValidationErrors({});
  };


  return (
    <>
    <DrawerProvider
      side="right"
      open={nudgeDrawerOpen || nudgeEditDrawerOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          // Close whichever drawer is open
          if (nudgeDrawerOpen) {
            setNudgeDrawerOpen(false);
          }
          if (nudgeEditDrawerOpen) {
            handleCloseEditDrawer();
          }
        }
      }}
    >
      <div className="relative min-h-screen">
        <div className="w-full sticky top-0 z-40 py-2 ">
          <div className="max-w-full mx-auto px-1">
            <TabSwitcher
              tabs={[
                { id: 'overview', label: 'Overview' },
                { id: 'nudges', label: 'Nudges' }
              ]}
              activeTab={activeTab}
              onTabChange={(tabId: string) => setActiveTab(tabId as 'overview' | 'nudges')}
              variant="horizontal"
            />
          </div>
        </div>

        <main className="p-1 space-y-3">
          {/* Overview Tab Content - Analytics Dashboard */}
          {activeTab === 'overview' && (
            <>
              {/* Drill-down detail view */}
              {drillDownCardId && (
                <div className="space-y-3">
                  {/* Header with Back, Title, Count, and Download */}
                  <div className="bg-white rounded-lg border shadow-sm p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleDrillDownBack}
                          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          <ArrowLeft className="w-5 h-5" />
                          <span className="text-sm font-medium">Back</span>
                        </button>
                        <div className="h-6 w-px bg-gray-300" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{drillDownTitle}</h3>
                          <p className="text-sm text-gray-700 font-medium">{drillDownTotalRows.toLocaleString()} total rows</p>
                        </div>
                      </div>
                      <button
                        onClick={handleDownloadCSV}
                        disabled={isDownloading}
                        className="download-csv-btn"
                        style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
                      >
                        <Download style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                        <span>{isDownloading ? 'Downloading...' : 'Download CSV'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Download error feedback */}
                  {downloadError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                      {downloadError}
                    </div>
                  )}

                  {/* Error state */}
                  {drillDownError && !isDrillDownLoading ? (
                    <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
                      <p className="text-red-600 mb-3">{drillDownError}</p>
                      <button
                        onClick={() => { retryDrillDown(); }}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors cursor-pointer"
                      >
                        Retry
                      </button>
                    </div>
                  ) : drillDownTotalRows === 0 && !isDrillDownLoading && drillDownRows.length === 0 ? (
                    <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
                      <p className="text-gray-500">No records found for this card.</p>
                    </div>
                  ) : (
                    /* Detail Table */
                    <AnalyticsPortalTable
                      title={drillDownTitle}
                      columns={drillDownColumns}
                      data={drillDownRows}
                      currentPage={drillDownPage}
                      pageSize={drillDownPageSize}
                      totalItems={drillDownTotalRows}
                      onPageChange={(page) => setDrillDownPage(page)}
                      onPageSizeChange={(size) => {
                        setDrillDownPageSize(size);
                        setDrillDownPage(1);
                      }}
                      isLoading={isDrillDownLoading}
                    />
                  )}
                </div>
              )}

              {/* Cards/Charts section - hidden via CSS when drilling down */}
              <div style={{ display: drillDownCardId ? 'none' : 'block' }} className="space-y-3">
                {/* Row 0: Cards skeleton */}
                {rowLoading[0] ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={`card-skeleton-${i}`} className="bg-white rounded-lg border shadow-sm p-4 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                      </div>
                    ))}
                  </div>
                ) : allItems[0] ? (() => {
                  const item = allItems[0];
                  if (item.type === 'card_row') {
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {item.data.map((card: CardConfig) => (
                          <AnalyticsCard
                            key={card.id}
                            {...card}
                            onClick={() => card.clickable && handleCardClick(card.id, card.title)}
                          />
                        ))}
                      </div>
                    );
                  } else if (item.type === 'table') {
                    return renderTableRow(item.data, 0);
                  } else if (item.type === 'mixed_row') {
                    return (
                      <div className="flex flex-wrap gap-2" style={{ alignItems: 'start' }}>
                        {item.data.map((rowItem: any) => {
                          if (rowItem.type === 'card') return <AnalyticsCard key={rowItem.id} {...rowItem} onClick={() => rowItem.clickable && handleCardClick(rowItem.id, rowItem.title)} />;
                          if (rowItem.type === 'mini_card') return <AnalyticsMiniCard key={rowItem.id} {...rowItem} onClick={() => rowItem.clickable && handleCardClick(rowItem.id, rowItem.title)} />;
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                })() : null}

                {/* Row 1: Mini cards skeleton */}
                {rowLoading[1] ? (
                  <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(80px,1fr))] lg:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={`mini-card-skeleton-${i}`} className="bg-white rounded-lg border shadow-sm p-3 animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
                        <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : allItems[1] ? (() => {
                  const item = allItems[1];
                  if (item.type === 'mini_card_row') {
                    return (
                      <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(80px,1fr))] lg:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
                        {item.data.map((miniCard: MiniCardConfig) => (
                          <AnalyticsMiniCard
                            key={miniCard.id}
                            {...miniCard}
                            onClick={() => miniCard.clickable && handleCardClick(miniCard.id, miniCard.title)}
                          />
                        ))}
                      </div>
                    );
                  } else if (item.type === 'card_row') {
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {item.data.map((card: CardConfig) => (
                          <AnalyticsCard key={card.id} {...card} onClick={() => card.clickable && handleCardClick(card.id, card.title)} />
                        ))}
                      </div>
                    );
                  } else if (item.type === 'mixed_row') {
                    return (
                      <div className="flex flex-wrap gap-2" style={{ alignItems: 'start' }}>
                        {item.data.map((rowItem: any) => {
                          if (rowItem.type === 'card') return <AnalyticsCard key={rowItem.id} {...rowItem} onClick={() => rowItem.clickable && handleCardClick(rowItem.id, rowItem.title)} />;
                          if (rowItem.type === 'mini_card') return <AnalyticsMiniCard key={rowItem.id} {...rowItem} onClick={() => rowItem.clickable && handleCardClick(rowItem.id, rowItem.title)} />;
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                })() : null}

                {/* Row 2: Charts skeleton */}
                {rowLoading[2] ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={`chart-skeleton-${i}`} className="bg-white rounded-lg border shadow-sm p-4 animate-pulse">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                        </div>
                        <div className="h-6 bg-gray-100 rounded w-1/4 mb-3"></div>
                        <div className="h-48 bg-gray-100 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : allItems[2] ? (() => {
                  const item = allItems[2];
                  if (item.type === 'chart_row') {
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {item.data.map((initialChartConfig: ChartConfig) => {
                          const chartConfig = chartDataMap.get(initialChartConfig.id) || initialChartConfig;
                          const isChartLoading = loadingCharts.has(initialChartConfig.id);

                          if (chartConfig.type === 'pie_chart') {
                            return (
                              <AnalyticsPieChart
                                key={chartConfig.id}
                                chartConfig={chartConfig}
                                ChartWrapper={ChartWrapper}
                                onButtonClick={async (buttonId) => {
                                  await handleChartButtonClick(initialChartConfig.id, buttonId);
                                }}
                                activeButtonId={activeButtonMap.get(chartConfig.id) || chartConfig.id}
                                isLoading={isChartLoading}
                              />
                            );
                          }
                          if (chartConfig.type === 'bar_chart') {
                            return (
                              <AnalyticsBarChart
                                key={chartConfig.id}
                                chartConfig={chartConfig}
                                ChartWrapper={ChartWrapper}
                                onButtonClick={async (buttonId) => {
                                  await handleChartButtonClick(initialChartConfig.id, buttonId);
                                }}
                                activeButtonId={activeButtonMap.get(chartConfig.id) || chartConfig.id}
                                isLoading={isChartLoading}
                              />
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                })() : null}
              </div>
            </>
          )}

          {/* Nudges Tab Content - Nudges Table */}
          {activeTab === 'nudges' && (
            <NudgesTable
              nudges={nudges}
              isLoading={isLoadingNudges}
              isExecutingNudge={isExecutingNudge}
              isDeletingNudge={isDeletingNudge}
              onExecuteNudge={handleExecuteNudge}
              onDeleteNudge={handleDeleteNudge}
              onEditNudge={handleEditNudge}
              onCreateNudge={() => setNudgeDrawerOpen(true)}
            />
          )}


        </main>

        {/* Nudge Drawer */}
        <NudgeDrawer
          onClose={() => setNudgeDrawerOpen(false)}
          nudgeForm={nudgeForm}
          setNudgeForm={setNudgeForm}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          onCreateNudge={handleCreateNudge}
          isCreating={isCreatingNudge}
          jsonTextState={jsonTextState}
          setJsonTextState={setJsonTextState}
          jsonValidationErrors={jsonValidationErrors}
          setJsonValidationErrors={setJsonValidationErrors}
          addUserTarget={addUserTarget}
          updateUserTarget={updateUserTarget}
          removeUserTarget={removeUserTarget}
          getJsonText={getJsonText}
          validateAndUpdateJson={validateAndUpdateJson}
          queryResults={queryResults}
          setQueryResults={setQueryResults}
          queryColumns={queryColumns}
          setQueryColumns={setQueryColumns}
          isSearching={isSearchingQuery}
          setIsSearching={setIsSearchingQuery}
          queryError={queryError}
          setQueryError={setQueryError}
        />

        {/* Edit Nudge Drawer */}
        {nudgeEditDrawerOpen && editingNudge && (
          <NudgeEditDrawer
            onClose={handleCloseEditDrawer}
            editingNudge={editingNudge}
            nudgeForm={nudgeForm}
            setNudgeForm={setNudgeForm}
            formErrors={formErrors}
            onUpdateNudge={handleUpdateNudge}
            isUpdating={isUpdatingNudge}
            jsonTextState={jsonTextState}
            setJsonTextState={setJsonTextState}
            jsonValidationErrors={jsonValidationErrors}
            setJsonValidationErrors={setJsonValidationErrors}
            addUserTarget={addUserTarget}
            updateUserTarget={updateUserTarget}
            removeUserTarget={removeUserTarget}
            getJsonText={getJsonText}
            validateAndUpdateJson={validateAndUpdateJson}
          />
        )}
      </div>
    </DrawerProvider>
    </>
  );
};

// Wrap with ErrorBoundary to prevent white screen on component crashes
export const AnalyticsPortalPageErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <AnalyticsPortalPage />
  </ErrorBoundary>
);

export default AnalyticsPortalPageErrorBoundary; 